import { useEffect, useState } from "react";
import { IonSegment, IonSegmentButton, IonLabel, IonList, IonItem, IonRadioGroup, IonRadio, IonInput, IonButton, IonIcon, IonSpinner } from "@ionic/react";
import { bookmarkOutline } from "ionicons/icons";
import SignaturePad, { SignatureBox, SignatureImage, useDarkMode } from "@/components/SignaturePad";
import UploadDropzone from "@/components/UploadDropzone";
import { useSavedAssets, AssetThumb, invalidateSavedAssets } from "@/components/SavedAssets";
import { readImageFile } from "@/utils/imageFile";
import { userHeaders } from "@/utils/userAuth";
import { toast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The sender's own signature (or initials) on a document they upload: one of
// the signatures saved under Branding, a fresh drawing, or an uploaded image.
// `value` is the image data URL the field carries; `onChange` gets the new
// one (or null when cleared). A drawing or upload can be kept in Branding
// for next time with the Save to Branding button.
export default function SenderSignaturePicker({ type = "signature", value, onChange }) {
  const dark = useDarkMode();
  const noun = type === "initials" ? "initials" : "signature";
  const { assets, loading } = useSavedAssets("signature");
  const [tab, setTab] = useState(null);     // saved | draw | upload; picked once the saved list is known
  const [draft, setDraft] = useState(null); // a drawing or upload not yet saved to Branding
  const [padKey, setPadKey] = useState(0);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  // open on Saved when there is anything saved (the current value selected if
  // it is one of them), else on Draw
  useEffect(() => {
    if (tab || loading) return;
    setTab(assets.length ? "saved" : "draw");
  }, [assets, loading, tab]);

  const pickedId = (assets.find((a) => a.data === value) || {}).id || "";

  const readFile = async (file) => {
    try {
      const data = await readImageFile(file, { label: type === "initials" ? "Initials" : "Signature", maxBytes: 1_400_000 });
      setDraft(data);
      onChange(data);
    } catch (err) { toast.error(err.message); }
  };

  const saveToBranding = async () => {
    const data = draft || value;
    if (!data) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/assets`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ kind: "signature", name: saveName.trim(), data }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
      toast.success("Saved to Branding");
      setSaveName("");
      invalidateSavedAssets();
      setTab("saved");
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === "draw") { setPadKey((k) => k + 1); setDraft(null); }
    if (t === "upload") setDraft(null);
  };

  if (!tab) return <div style={{ display: "flex", justifyContent: "center", padding: 20 }}><IonSpinner name="crescent" /></div>;
  return (
    <div className="sender-sig" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <IonSegment mode="ios" value={tab} onIonChange={(e) => switchTab(e.detail.value)}>
        {assets.length > 0 && <IonSegmentButton value="saved"><IonLabel>Saved</IonLabel></IonSegmentButton>}
        <IonSegmentButton value="draw"><IonLabel>Draw</IonLabel></IonSegmentButton>
        <IonSegmentButton value="upload"><IonLabel>Upload</IonLabel></IonSegmentButton>
      </IonSegment>

      {tab === "saved" && (
        /* the Branding signatures by name, the one in the field selected */
        <IonRadioGroup value={pickedId} onIonChange={(e) => { const a = assets.find((x) => x.id === e.detail.value); if (a) onChange(a.data); }}>
          <IonList lines="full" style={{ padding: 0, borderRadius: 10, overflow: "hidden", border: "1px solid var(--ion-border-color, rgba(0,0,0,0.12))" }}>
            {assets.map((a, i) => (
              <IonItem key={a.id} lines={i === assets.length - 1 ? "none" : undefined} className="pm-row" button detail={false} onClick={() => onChange(a.data)}>
                <AssetThumb asset={a} dark={dark} />
                <IonLabel>
                  {a.name}
                  {a.isDefault && <p>Default</p>}
                </IonLabel>
                <IonRadio slot="end" value={a.id} aria-label={`Use ${a.name}`} />
              </IonItem>
            ))}
          </IonList>
        </IonRadioGroup>
      )}

      {tab === "draw" && (
        <>
          <SignaturePad key={padKey} height={160} onChange={(dataUrl) => { setDraft(dataUrl); onChange(dataUrl); }} dark={dark} />
          <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--ion-color-medium)" }}>Draw your {noun} with a finger, a stylus or the mouse.</p>
        </>
      )}

      {tab === "upload" && (
        value && (draft || !pickedId) ? (
          <>
            <SignatureBox height={150}><SignatureImage src={value} dark={dark} /></SignatureBox>
            <IonButton expand="block" color="light" onClick={() => { setDraft(null); onChange(null); }} style={{ margin: 0 }}>Choose another image</IonButton>
          </>
        ) : (
          <UploadDropzone accept="image/*" onFile={readFile} />
        )
      )}

      {(tab === "draw" || tab === "upload") && (
        /* keep this one under Branding for next time: the name, then the button, each full width */
        <>
          <IonInput fill="outline" labelPlacement="floating" label="Name (optional)" value={saveName} maxlength={40} onIonInput={(e) => setSaveName(e.detail.value ?? "")} />
          <IonButton expand="block" disabled={!(draft || (tab === "upload" && value && !pickedId)) || saving} onClick={saveToBranding} style={{ margin: 0 }}>
            {saving ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <><IonIcon slot="start" icon={bookmarkOutline} /> Save to Branding</>}
          </IonButton>
        </>
      )}
    </div>
  );
}
